// @ts-strict-ignore
import { Component, DoCheck, Inject, Input } from "@angular/core";
import { FormBuilder } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { ModalController } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";

import { AbstractFlatWidget } from "src/app/shared/components/flat/abstract-flat-widget";
import { DataService } from "src/app/shared/components/shared/dataservice";
import { Service } from "src/app/shared/service/service";
import { UserService } from "src/app/shared/service/user.service";
import { Websocket } from "src/app/shared/service/websocket";
import { ChannelAddress } from "src/app/shared/shared";

@Component({
    selector: "Controller_Energy_CostRevenue_Modal",
    templateUrl: "./modal.html",
    standalone: false,
})
export class ModalComponent extends AbstractFlatWidget implements DoCheck {

    @Input({ required: true }) public override componentId!: string;

    /**
     * OpenEMS oe-modal needs a FormGroup for editable configuration lines.
     *
     * PV/ESS kWh costs are configuration values and can therefore be edited here.
     * Calculated channels like GridBuyCost remain read-only.
     */
    protected readonly costRevenueFormGroup = this.formBuilder.group({
        pvCostEurPerKwh: [0.08],
        essCostEurPerKwh: [0.05],
    });

    private configFormInitialized = false;

    constructor(
        @Inject(Websocket) protected override websocket: Websocket,
        @Inject(ActivatedRoute) protected override route: ActivatedRoute,
        @Inject(Service) protected override service: Service,
        @Inject(ModalController) protected override modalController: ModalController,
        @Inject(TranslateService) protected override translate: TranslateService,
        protected override dataService: DataService,
        protected override formBuilder: FormBuilder,
        protected override router: Router,
        protected override userService: UserService,
    ) {
        super(websocket, route, service, modalController, translate, dataService, formBuilder, router, userService);
    }


    public ngDoCheck(): void {
        if (this.configFormInitialized || this.component == null) {
            return;
        }

        const properties = this.component.properties ?? {};

        this.costRevenueFormGroup.patchValue({
            pvCostEurPerKwh: this.toNumber(properties["pvCostEurPerKwh"], 0.08),
            essCostEurPerKwh: this.toNumber(properties["essCostEurPerKwh"], 0.05),
        });

        this.configFormInitialized = true;
    }

    private toNumber(value: unknown, fallback: number): number {
        if (typeof value === "number") {
            return value;
        }
        if (typeof value === "string") {
            const parsed = Number(value);
            return Number.isFinite(parsed) ? parsed : fallback;
        }
        return fallback;
    }

    protected override getChannelAddresses(): ChannelAddress[] {
        return [
            // Current cycle values
            new ChannelAddress(this.componentId, "GridBuyCost"),
            new ChannelAddress(this.componentId, "GridSellRevenue"),
            new ChannelAddress(this.componentId, "GridSellCost"),
            new ChannelAddress(this.componentId, "ActualGridOnlyNetCost"),
            new ChannelAddress(this.componentId, "CashflowSavingsVsAllFromGrid"),
            new ChannelAddress(this.componentId, "ActualTotalCost"),
            new ChannelAddress(this.componentId, "AllFromGridCost"),
            new ChannelAddress(this.componentId, "SavingsVsAllFromGrid"),

            // Daily energy
            new ChannelAddress(this.componentId, "DailyGridBuyEnergyWh"),
            new ChannelAddress(this.componentId, "DailyGridSellEnergyWh"),
            new ChannelAddress(this.componentId, "DailyProductionEnergyWh"),
            new ChannelAddress(this.componentId, "DailyConsumptionEnergyWh"),
            new ChannelAddress(this.componentId, "DailySelfSuppliedEnergyWh"),
            new ChannelAddress(this.componentId, "DailyEssChargeEnergyWh"),
            new ChannelAddress(this.componentId, "DailyEssDischargeEnergyWh"),

            // Daily costs/revenues
            new ChannelAddress(this.componentId, "DailyGridBuyCost"),
            new ChannelAddress(this.componentId, "DailyGridSellRevenue"),
            new ChannelAddress(this.componentId, "DailyGridSellCost"),
            new ChannelAddress(this.componentId, "DailyActualGridOnlyNetCost"),
            new ChannelAddress(this.componentId, "DailyCashflowSavingsVsAllFromGrid"),
            new ChannelAddress(this.componentId, "DailyPvEnergyCost"),
            new ChannelAddress(this.componentId, "DailyEssDischargeCost"),
            new ChannelAddress(this.componentId, "DailyActualTotalCost"),
            new ChannelAddress(this.componentId, "DailyAllFromGridCost"),
            new ChannelAddress(this.componentId, "DailySavingsVsAllFromGrid"),

            // Total counters
            new ChannelAddress(this.componentId, "TotalActualTotalCost"),
            new ChannelAddress(this.componentId, "TotalAllFromGridCost"),
            new ChannelAddress(this.componentId, "TotalCashflowSavingsVsAllFromGrid"),
            new ChannelAddress(this.componentId, "TotalSavingsVsAllFromGrid"),
            new ChannelAddress(this.componentId, "TotalGridBuyEnergyWh"),
            new ChannelAddress(this.componentId, "TotalGridSellEnergyWh"),
            new ChannelAddress(this.componentId, "TotalProductionEnergyWh"),
            new ChannelAddress(this.componentId, "TotalConsumptionEnergyWh"),
        ];
    }

    protected readonly energyKwhConverter = (value: number | null): string => {
        if (value == null) {
            return "-";
        }
        return `${(value / 1000).toFixed(2)} kWh`;
    };

    protected readonly eurConverter = (value: number | null): string => {
        if (value == null) {
            return "-";
        }
        return `${value.toFixed(2)} €`;
    };

    protected readonly eurSmallConverter = (value: number | null): string => {
        if (value == null) {
            return "-";
        }
        return `${value.toFixed(4)} €`;
    };

    
}
